import styled, { css } from 'styled-components'

import type { TradeType } from '@root/entities/trade-type'
import type { ClassNameProps } from '@root/shared/react'

export type TradingSliderTicksProps = {
  type: TradeType.SwapDirection
  value: number
  ticks: [number, number, number, number, number]
  forceSign?: boolean
  onClick: (percentage: number) => void
} & ClassNameProps

export const TradingSliderTicks: React.FC<TradingSliderTicksProps> = ({
  value,
  ticks,
  onClick,
  className,
  type,
  forceSign = false,
}) => {
  const percentage = Number(value) || 0
  const min = ticks[0]
  const max = ticks[4]

  const [offsetLeft, offsetRight] =
    type === 'buy' ? ['-10%', '10%'] : ['-20%', '20%']

  const inRange = (tick: number) =>
    tick - 10 <= percentage && percentage <= tick + 10

  const sign = (tick: number) => (forceSign ? (tick > 0 ? '+' : '') : '')

  return (
    <Root className={className}>
      <Tick $active={inRange(min)} onClick={() => onClick(min)}>
        {sign(min)}
        {min}%
      </Tick>
      <Tick
        $active={inRange(ticks[1])}
        onClick={() => onClick(ticks[1])}
        css={css`
          transform: translateX(${offsetLeft});
        `}
      >
        {sign(ticks[1])}
        {ticks[1]}%
      </Tick>
      <Tick
        $active={inRange(ticks[2])}
        onClick={() => onClick(ticks[2])}
        css={css`
          transform: translateX(3px);
        `}
      >
        {sign(ticks[2])}
        {ticks[2]}%
      </Tick>
      <Tick
        $active={inRange(ticks[3])}
        onClick={() => onClick(ticks[3])}
        css={css`
          transform: translateX(${offsetRight});
        `}
      >
        {sign(ticks[3])}
        {ticks[3]}%
      </Tick>
      <Tick $active={inRange(max)} onClick={() => onClick(max)}>
        {sign(max)}
        {max}%
      </Tick>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;

  :nth-child(1) {
    justify-self: start;
    width: 100%;
    align-items: start;
    justify-content: start;
    display: grid;
  }
  :nth-last-child(1) {
    justify-self: end;
    width: 100%;
    align-items: end;
    justify-content: end;
    display: grid;
  }
`

const Tick = styled.div<{ $active: boolean }>`
  color: ${({ $active }) =>
    $active ? 'var(--background-11)' : 'var(--background-7)'};
  text-align: center;

  font-size: 11px;
  font-weight: 700;
  line-height: 16px;

  cursor: pointer;
`
